/**
 * Utility functions for handling images
 */

/**
 * Resizes an image file while maintaining aspect ratio
 * @param {File} file - The original image File object
 * @param {number} maxWidth - The maximum width of the resized image
 * @param {number} maxHeight - The maximum height of the resized image
 * @param {number} quality - The quality of the resized image (0-1)
 * @returns {Promise<File>} - A promise that resolves to the resized File
 */
export const resizeImage = (
    file,
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.85
) => {
    return new Promise((resolve, reject) => {
        // Create a FileReader to read the file
        const reader = new FileReader();

        // Set up the onload event handler
        reader.onload = (readerEvent) => {
            // Create an image element to calculate dimensions
            const img = new Image();
            img.onload = () => {
                // Calculate new dimensions while maintaining aspect ratio
                let width = img.width;
                let height = img.height;

                // Only resize if the image is larger than our max dimensions
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(
                        maxWidth / width,
                        maxHeight / height
                    );
                    width = Math.floor(width * ratio);
                    height = Math.floor(height * ratio);
                } else {
                    // If image is already smaller than max dimensions, return original
                    resolve(file);
                    return;
                }

                // Create a canvas to draw the resized image
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                // Draw the image on the canvas
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Convert the canvas to a Blob
                canvas.toBlob(
                    (blob) => {
                        // Create a new File from the blob
                        const resizedFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now(),
                        });

                        // Resolve with the new file
                        resolve(resizedFile);
                    },
                    'image/jpeg',
                    quality
                );
            };

            // Set the image source to the FileReader result
            img.src = readerEvent.target.result;
        };

        // Handle errors
        reader.onerror = (error) => reject(error);

        // Read the file as a data URL
        reader.readAsDataURL(file);
    });
};

/**
 * Batch resize multiple image files
 * @param {File[]} files - Array of image File objects
 * @param {number} maxWidth - The maximum width of the resized images
 * @param {number} maxHeight - The maximum height of the resized images
 * @param {number} quality - The quality of the resized images (0-1)
 * @returns {Promise<File[]>} - A promise that resolves to an array of resized Files
 */
export const resizeImageBatch = async (
    files,
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.85
) => {
    const resizedFiles = [];

    for (const file of files) {
        // Only process image files
        if (file.type.startsWith('image/')) {
            const resizedFile = await resizeImage(
                file,
                maxWidth,
                maxHeight,
                quality
            );
            resizedFiles.push(resizedFile);
        } else {
            // If not an image, keep the original file
            resizedFiles.push(file);
        }
    }

    return resizedFiles;
};
